import { createTheme } from '@mui/material/styles';
import { Theme as MuiTheme } from '@mui/material/styles'; // Import the base MUI Theme type
import type {} from '@mui/x-date-pickers/themeAugmentation';


// 1. Define the interfaces for your custom MUI palette properties
// This tells TypeScript that theme.palette will have these custom sections.
declare module '@mui/material/styles' {
  interface CustomPalette {
    white: {
      main: string;
    };
    custom: {
      sidebarBg: string;
      sidebarText: string;
      activeBg: string;
      iconColor: string;
      link: string;
      lightGrey: string;
      borderRadius: number;
    };
    link: {
      main: string;
    };
  }
  interface Palette extends CustomPalette { }
  interface PaletteOptions extends CustomPalette { }
}

// 2. Define the interface for your original, non-MUI properties
// This is your current theme structure.
interface OriginalTheme {
  colors: {
    primary: string;
    secondary: string;
    darkText: string;
    white: string;
    lightGrey: string;
    link: string;
    error: string;
    mainBackground: string;
    warning: string;
  };
  fonts: {
    primary: string;
  };
  spacing: (n: number) => string;
}

// 3. Define the final combined theme type
export type AppTheme = OriginalTheme & MuiTheme;


// 4. YOUR ORIGINAL THEME DATA (for backward compatibility)
const originalTheme: OriginalTheme = {
  colors: {
    primary: '#479577',
    secondary: '#E1EBE3',
    darkText: '#333333',
    white: '#FFFFFF',
    lightGrey: '#565D6D',
    link: '#4A90E2',
    error: '#FF4D4F',
    mainBackground: '#F8F9FB',
    warning: '#D2A627',
  },
  fonts: {
    primary: 'Arial, sans-serif',
  },
  spacing: (n: number) => `${n * 6.4}px`, // Reduced from 8px to match 0.8 zoom reduction
};

// --- Shared Constants ---
export const drawerWidth = 240; // Slightly reduced from 280
export const collapsedDrawerWidth = 70; // Slightly reduced from 80
export const primaryColor = originalTheme.colors.primary;
export const secondaryColor = originalTheme.colors.secondary;
export const warning = originalTheme.colors.warning;
const primaryDark = '#3b7b62';


// 5. Create the MUI Theme
export let muiTheme = createTheme({
  palette: {
    primary: {
      main: primaryColor,
      dark: primaryDark,
      light: originalTheme.colors.secondary,
      contrastText: originalTheme.colors.white,
    },
    secondary: {
      main: secondaryColor,
      contrastText: originalTheme.colors.darkText,
    },
    error: {
      main: originalTheme.colors.error,
    },
    warning: {
      main: originalTheme.colors.warning,
    },
    white: {
      main: originalTheme.colors.white,
    },
    custom: {
      sidebarBg: originalTheme.colors.white,
      sidebarText: originalTheme.colors.darkText,
      activeBg: originalTheme.colors.secondary,
      iconColor: originalTheme.colors.lightGrey,
      link: originalTheme.colors.link,
      lightGrey: originalTheme.colors.lightGrey,
      borderRadius: 10,
    },
    text: {
      primary: originalTheme.colors.darkText,
    },
    background: {
      default: originalTheme.colors.mainBackground,
    },
    // We use your lightGrey for MUI's grey[300]
    grey: {
      300: originalTheme.colors.lightGrey,
    } as any,
    link: {
      main: originalTheme.colors.link
    }
  },
  typography: {
    fontFamily: originalTheme.fonts.primary,
    fontSize: 12, // Reduced base font size from default 14
    htmlFontSize: 16, // Browser default
    button: {
      textTransform: 'none', // Prevents redundant caps forcing larger visuals
      fontSize: '0.8rem',
    },
    body1: {
      fontSize: '1.05rem',
    },
    body2: {
      fontSize: '0.85rem',
    },
    h1: { fontSize: '2.5rem' },
    h2: { fontSize: '2.0rem' },
    h3: { fontSize: '1.75rem' },
    h4: { fontSize: '1.5rem' },
    h5: { fontSize: '1.25rem' },
    h6: { fontSize: '1.0rem' },
  },
  spacing: 6.4, // MUI uses this for spacing() calls internally
  components: {
    MuiDateCalendar: {
      styleOverrides: {
        root: {
          width: '300px',
          maxHeight: 'none',
        },
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: {
          fontSize: '14px',
          width: '38px',
          height: '38px',
        },
      },
    },
    MuiDayCalendar: {
      styleOverrides: {
        weekDayLabel: {
          fontSize: '13px',
          width: '38px',
        },
        monthContainer: {
          minHeight: '220px',
        },
      },
    },
    MuiPickersCalendarHeader: {
      styleOverrides: {
        label: {
          fontSize: '16px',
        },
      },
    },
    MuiDigitalClock: {
      styleOverrides: {
        root: {
          '& .MuiMenuItem-root': {
            fontSize: '14px',
          },
        },
      },
    },
    MuiMultiSectionDigitalClock: {
      styleOverrides: {
        root: {
          '& .MuiMenuItem-root': {
            fontSize: '14px',
          },
        },
      },
    },
  },
});


// 6. EXPORT THE COMBINED THEME
// We use object spreading to merge the MUI theme with your original theme structure.
// This allows both:
// 1. New components: theme.palette.custom.sidebarBg (MUI style)
// 2. Old components: theme.colors.primary (Original style)
export const theme = {
  ...muiTheme,
  ...originalTheme
} as AppTheme;